import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { FC, useEffect, useState, useRef } from 'react';
import { TableCell, TableRow } from '@mui/material';
import {
  ConfirmationModalContent,
  ErrorContainer,
  InputField,
  ModalPopup,
  Table,
  Tooltip
} from 'components';
import { HeadCell } from 'components/table/table';
import { ClickAwayListener } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { resetFilesList } from 'store/files/filesSlice';
import { getFiles, deleteFile, putFile, FilesItem } from 'store/files/filesActions';
import { axiosPrivate } from 'modules/networkTools/networkTools';
import {
  getFilesSortForClient,
  setFilesSortForClient
} from 'modules/networkTools/localStorageTokens';

import style from './files.module.scss';

const headCells: HeadCell[] = [
  {
    id: 'uploadDateTime',
    label: 'Date and Time',
    sort: true
  },
  {
    id: 'fileName',
    label: 'File Name',
    sort: true,
    width: '60%'
  },
  {
    id: 'ownerFirstName',
    label: 'Owner',
    sort: true
  }
];

const downloadFile = async (clientId: string, item: FilesItem): Promise<void> => {
  if (!clientId) return;

  // request the file as a blob
  const response = await axiosPrivate.get(`client/${clientId}/attachments/${item.id}/download`, {
    responseType: 'blob'
  });

  // response.data is a Blob when responseType is 'blob'
  const blob = response.data as Blob;
  const url = window.URL.createObjectURL(blob);

  const element = document.createElement('a');
  element.href = url;
  element.download = `${item.fileName}.${item.fileExtension}`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);

  // free the object URL
  window.URL.revokeObjectURL(url);
};

const TableCellValue: FC<{
  value?: string | null;
  onDoubleClick?: () => void;
  maxWidth?: string;
}> = ({ value, onDoubleClick, maxWidth = '120px' }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const { scrollWidth, offsetWidth } = textRef.current;
      setIsTextTruncated(scrollWidth > offsetWidth);
    }
  }, [value]);

  const cursor = onDoubleClick ? 'text' : 'default';

  return (
    <TableCell sx={{ maxWidth, cursor }} onDoubleClick={onDoubleClick}>
      <div ref={textRef}>
        {isTextTruncated ? (
          <Tooltip
            toolTipText={isTextTruncated ? value : ''}
            disableInteractive
            blackTooltip
            placement="bottom-start"
            enterDelay={300}
          >
            <span className={clsx(style.valueWrap, style.truncatedText)}>{value}</span>
          </Tooltip>
        ) : (
          <span className={style.valueWrap}>{value}</span>
        )}
      </div>
    </TableCell>
  );
};

interface Props {}

const MedicalVisitsTable: FC<Props> = () => {
  const { clientId = '' } = useParams();
  const dispatch = useAppDispatch();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [entered, setEntered] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<FilesItem | null>(null);
  const { filesList, filesListLoading } = useAppSelector((state) => state.files);
  const { content, page, size, sorts } = filesList;
  const tableRef = useRef<HTMLDivElement>(null);

  const fileNames = filesList.content?.map((file) => `${file.fileName}.${file.fileExtension}`);
  const savedSorts = getFilesSortForClient(clientId);

  const getTableData = (data: any = {}) => {
    dispatch(getFiles({ clientId, page, size, ...data }));
  };

  useEffect(() => {
    getTableData({ sorts: savedSorts || sorts });

    return () => {
      dispatch(resetFilesList());
    };
  }, [clientId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...selectedFile,
      fileName: selectedFile?.fileName ?? ''
    },
    validationSchema: Yup.object().shape({
      fileName: Yup.string()
        .nullable()
        .required('File Name is required.')
        .test('File Name', 'File with the same name already exists.', function (fileName, context) {
          const originalFileName = selectedFile?.fileName;
          if (
            fileName &&
            fileName !== originalFileName &&
            fileNames.includes(`${fileName}.${context.parent.fileExtension}`)
          ) {
            return false;
          }
          return true;
        })
    }),
    onSubmit: (values) => {
      if (selectedFile?.id) {
        const data = values;
        dispatch(putFile({ clientId, fileId: selectedFile?.id, data })).then((res: any) => {
          if (res.error) {
            getTableData();
          }
          if (entered) {
            setEditMode(false);
            setSelectedFile(null);
            setEntered(false);
          }
        });
      }
    }
  });

  const onCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedFile(null);
    setEntered(false);
  };

  const handleDelete = async () => {
    if (selectedFile?.id) {
      const res: any = await dispatch(deleteFile({ clientId, fileId: selectedFile.id }));
      if (!res.error) {
        onCancelDelete();
      }
    }
  };

  return (
    <>
      <Table
        ref={tableRef}
        tableWrapClassName={style.tableWrap}
        totalNumber={0}
        page={0}
        size={size}
        sorts={savedSorts || sorts}
        handleOnSort={(sortConfig: any) => {
          setFilesSortForClient(clientId, sortConfig);
        }}
        isLoading={filesListLoading}
        headCells={headCells}
        getTableData={getTableData}
        showEmptyHeader={true}
        columns={content.map((row) => (
          <TableRow
            key={row.id}
            data-test-id="files-table-row"
            id={`row-${row.id}`}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') {
                setEntered(false);
              }
              if (e.key === 'Enter') {
                setEntered(true);
                formik.handleSubmit();
              }
            }}
          >
            <TableCellValue value={dayjs(row.uploadDateTime).format('MM/DD/YY h:mm a')} />
            <ClickAwayListener
              onClickAway={(event) => {
                if (editMode && selectedFile?.id === row.id) {
                  const target = event.target as Element;
                  if (
                    !target.closest(`.${style.editMode}`) ||
                    !target.closest(`.${style.inputField}`)
                  ) {
                    setEditMode(false);
                    setSelectedFile(null);
                  }
                }
              }}
            >
              {editMode && selectedFile?.id === row.id ? (
                <TableCell>
                  <InputField
                    classnamesProps={style.inputField}
                    id="fileName"
                    name="fileName"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={(e: any) => {
                      formik.handleBlur(e);
                      formik.submitForm();
                    }}
                    value={formik.values.fileName || ''}
                  />
                  {formik.errors.fileName && (
                    <ErrorContainer
                      autoHeight
                      visible={formik.errors.fileName}
                      name="fileName"
                      errors={formik.errors}
                    />
                  )}
                </TableCell>
              ) : (
                <TableCellValue
                  onDoubleClick={() => {
                    setEditMode(true);
                    setSelectedFile(row);
                  }}
                  value={`${row?.fileName}.${row?.fileExtension}`}
                  maxWidth="140px"
                />
              )}
            </ClickAwayListener>
            <TableCell>
              <div className={style.iconWrap}>
                <span>
                  {row.ownerFirstName} {row.ownerLastName}
                </span>
                <div>
                  <span
                    onClick={() => {
                      if (clientId) {
                        downloadFile(clientId, row);
                      }
                    }}
                    className={clsx('material-icons', style.icon)}
                  >
                    download
                  </span>
                  <span
                    onClick={() => {
                      setDeleteModalOpen(true);
                      setSelectedFile(row);
                    }}
                    className={clsx('material-icons', style.icon)}
                  >
                    delete
                  </span>
                </div>
              </div>
            </TableCell>
          </TableRow>
        ))}
      />
      {!filesListLoading && content.length === 0 && (
        <div className={style.noData}>
          <p>Nothing to see here!</p>
        </div>
      )}
      <ModalPopup
        open={deleteModalOpen}
        customClassInsideContainer={style.modalDelete}
        handleClose={onCancelDelete}
      >
        <ConfirmationModalContent
          confirmButtonText="Yes, delete"
          rejectButtonText="Cancel"
          title="Are you sure you want to delete this file?"
          text=" "
          handleConfirm={handleDelete}
          handleReject={onCancelDelete}
        />
      </ModalPopup>
    </>
  );
};

export default MedicalVisitsTable;
